import { useState, useMemo, Fragment } from "react";
import { Icon } from "semantic-ui-react";
import { makeStyles } from "@mui/styles";

import { useIntl } from "react-intl";

type CardSymptomInfoProps = {
  answers?: string;
  note?: string;
  editable?: boolean;
  onEdit?: (isAppointNote?: boolean) => void;
};

const COLORS = {
  grey: "#3C3C3C",
};

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: "5px",
    padding: "12px 14px 10px",
    color: COLORS.grey,
    marginBottom: "12px",
    border: "1px solid rgba(217, 217, 217, 1)",
    lineHeight: 1.675,
  },
}));

const formatFormAnswers = (data: Record<string, any>[]) => {
  const result = [];

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const next = data[i + 1];

    // ข้ามคำถามย่อย (ที่ไม่มี options คือไม่ใช่ Select หลัก)
    if (item.nullOk && !item.options) continue;

    // ตรวจสอบว่าต้องใช้คำตอบจากข้อถัดไปหรือไม่
    // (กรณี Select ที่มีรายละเอียดเพิ่มเติม เช่น มีโรคประจำตัว -> ระบุโรค)
    const useNext =
      item.type === "Select" && next?.nullOk && next?.value?.trim();
    // ถ้าใช้คำตอบจากข้อถัดไป ให้ตัด "มี", "เคย", "หรือไม่" ออกจาก label
    const label = useNext
      ? item.label.replace(/^มี|^เคย|หรือไม่$/g, "").trim()
      : item.label;

    result.push({
      label: label.replace(/ที่พบ$/g, "").trim(),
      value: useNext ? next.value.trim() : String(item.value).trim(),
    });

    if (useNext) {
      i++;
    }
  }

  return result;
};

const CardSymptomInfo = (props: CardSymptomInfoProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const [showAllAnswers, setShowAllAnswers] = useState(false);

  const formattedAnswers = useMemo(() => {
    const answers = JSON.parse(props.answers || "{}");
    const extraItems = answers[0]?.extra_items ?? [];
    const isAppointNote = !props.answers && !!props.note;

    if (isAppointNote) {
      return {
        form: [
          {
            label: intl.formatMessage({ id: "วัตถุประสงค์ในการนัดหมาย" }),
            value: props.note?.trim() || "\u00A0",
          },
        ],
        isAppointNote,
      };
    }

    return {
      // symptomName: extraItems[0]?.value || "",
      isAppointNote,
      form: formatFormAnswers(extraItems),
    };
  }, [props.answers, props.note, intl.locale]);

  if (formattedAnswers.form.length === 0) {
    return null;
  }

  return (
    <div className={classes.card} style={{ marginTop: "1rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "bold",
          position: "relative",
        }}
      >
        {/* <div>ลักษณะอาการเบื้องต้น</div> */}
        {props.editable && (
          <div
            style={{
              color: "var(--primary-theme-color)",
              position: "absolute",
              right: 0,
            }}
            onClick={() => {
              props.onEdit?.(formattedAnswers.isAppointNote);
            }}
          >
            {intl.formatMessage({ id: "แก้ไข" })}
          </div>
        )}
      </div>
      <div style={{ marginTop: "0.25rem" }}>
        <>
          {(showAllAnswers
            ? formattedAnswers.form
            : [formattedAnswers.form[0]].filter(Boolean)
          ).map((item, index) => (
            <Fragment key={index}>
              <div
                style={{
                  marginTop: "0.25rem",
                  fontWeight: showAllAnswers ? "500" : "bold",
                }}
              >
                {item.label}
              </div>
              <div style={{ marginLeft: "1rem" }}>{item.value}</div>
            </Fragment>
          ))}
        </>
      </div>
      {!formattedAnswers.isAppointNote && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0.25rem",
          }}
        >
          <div
            style={{
              color: "var(--primary-theme-color)",
              WebkitTapHighlightColor: "transparent",
            }}
            onClick={() => setShowAllAnswers(!showAllAnswers)}
          >
            <Icon name={showAllAnswers ? "chevron up" : "chevron down"} />
            <label>
              {showAllAnswers
                ? intl.formatMessage({ id: "ย่อ" })
                : intl.formatMessage({ id: "แสดงข้อมูล" })}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardSymptomInfo;
